import type {
  CareReviewCounts,
  CareReviewListStatus,
  CareReviewTodo,
  CareUser,
} from "@/lib/api/care";
import {
  daysOpen,
  menteeListLabel,
  presentationChanged,
  type CareReviewSort,
  type CareReviewSortColumn,
} from "@/lib/care-reviews/review-list";
import { useDevOverridesStore } from "@/lib/hooks/admin/use-dev-overrides";
import { formatCompactAge, formatShortDate } from "@/lib/format-date";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { cn } from "@/lib/utils";
import { ChevronDown, ClipboardPen, Repeat, Search } from "lucide-react";
import { DateTime } from "luxon";
import type { ReactNode } from "react";

export function CareReviewToolbar({
  query,
  onQueryChange,
  status,
  counts,
  onStatusChange,
  filters,
}: {
  query: string;
  onQueryChange: (value: string) => void;
  status: CareReviewListStatus;
  counts: CareReviewCounts;
  onStatusChange: (status: CareReviewListStatus) => void;
  filters?: ReactNode;
}) {
  return (
    <div className="flex shrink-0 flex-col gap-4">
      <div className="flex items-stretch gap-[18px]">
        <label className="flex h-14 min-w-0 flex-1 items-center gap-3 rounded-xl border border-[#8a8275] bg-white px-4">
          <Search className="size-6 shrink-0 text-[#716a60]" aria-hidden />
          <input
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Search teen"
            className="w-full bg-transparent font-body text-base leading-6 text-[#0e0d0c] outline-none placeholder:text-[#716a60]"
          />
        </label>
        <div className="flex h-14 flex-1 items-center rounded-full bg-[#e5e0d2] p-1">
          <StatusSegment
            active={status === "open"}
            label={`Open (${counts.open})`}
            onClick={() => onStatusChange("open")}
          />
          <StatusSegment
            active={status === "closed"}
            label={`Closed (${counts.closed})`}
            onClick={() => onStatusChange("closed")}
          />
        </div>
      </div>
      {filters}
    </div>
  );
}

const COLUMN_WIDTH: Record<CareReviewSortColumn, string> = {
  mentee: "minmax(0,1.5fr)",
  cycle: "116px",
  age: "minmax(96px,0.8fr)",
  submitted: "minmax(96px,0.8fr)",
  assigned: "minmax(148px,1fr)",
};

const MONTHS_TO_SHOW = 24;

function monthOptions(selected: string): string[] {
  const latest = DateTime.utc().startOf("month");
  const months = Array.from({ length: MONTHS_TO_SHOW }, (_, index) =>
    latest.minus({ months: index }).toFormat("yyyy-MM"),
  );
  return months.includes(selected) ? months : [selected, ...months];
}

function monthLabel(month: string): string {
  const parsed = DateTime.fromFormat(month, "yyyy-MM", { zone: "utc" });
  return parsed.isValid ? parsed.toFormat("LLLL yyyy") : month;
}

export function CareReviewClosedFilters({
  month,
  onMonthChange,
  supervisorId,
  onSupervisorChange,
  supervisors,
}: {
  month: string;
  onMonthChange: (month: string) => void;
  supervisorId: string;
  onSupervisorChange: (supervisorId: string) => void;
  supervisors: { id: string; name: string }[];
}) {
  return (
    <div className="flex gap-3">
      <Select value={month} onValueChange={onMonthChange}>
        <SelectTrigger
          className="h-14 min-w-0 flex-1 border-buttonBorder-tertiary bg-surfaces-z0 px-5 text-body-1 text-text-tertiary shadow-none"
          aria-label="Month"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="rounded-2xl border-[#cbc6be] font-body">
          {monthOptions(month).map((option) => (
            <SelectItem key={option} value={option}>
              {monthLabel(option)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={supervisorId || "all"}
        onValueChange={(value) =>
          onSupervisorChange(value === "all" ? "" : value)
        }
      >
        <SelectTrigger
          className="h-14 min-w-0 flex-1 border-buttonBorder-tertiary bg-surfaces-z0 px-5 text-body-1 text-text-tertiary shadow-none"
          aria-label="Supervisor"
        >
          <SelectValue placeholder="All supervisors" />
        </SelectTrigger>
        <SelectContent className="rounded-2xl border-[#cbc6be] font-body">
          <SelectItem value="all">All supervisors</SelectItem>
          {supervisors.map((supervisor) => (
            <SelectItem key={supervisor.id} value={supervisor.id}>
              {supervisor.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function StatusSegment({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex h-12 flex-1 items-center justify-center rounded-full px-4 font-body text-sm font-bold leading-4",
        active
          ? "bg-white text-[#0e0d0c] shadow-[0px_3px_6px_rgba(0,0,0,0.13)]"
          : "text-[#58534b]",
      )}
    >
      {label}
    </button>
  );
}

export function CareReviewTable({
  reviews,
  columns,
  sort,
  onSort,
  selectedId,
  onSelect,
  showMarker,
  showOpenDays,
  now,
  className,
}: {
  reviews: CareReviewTodo[];
  columns: { id: CareReviewSortColumn; label: string }[];
  sort: CareReviewSort;
  onSort: (column: CareReviewSortColumn) => void;
  selectedId: string | null;
  onSelect: (review: CareReviewTodo) => void;
  showMarker: boolean;
  showOpenDays: boolean;
  now: DateTime;
  className?: string;
}) {
  const hasAssigned = columns.some((column) => column.id === "assigned");
  const template = `${showMarker ? "32px " : ""}${columns
    .map((column) =>
      column.id === "age" && hasAssigned ? "minmax(168px,0.8fr)" : COLUMN_WIDTH[column.id],
    )
    .join(" ")}`;

  return (
    <div
      className={cn(
        "flex min-h-0 flex-col overflow-hidden rounded-2xl border border-[#cbc6be] bg-white",
        className,
      )}
    >
      <div className="min-h-0 flex-1 overflow-y-auto">
      <div
        className="sticky top-0 z-10 grid bg-[#f6f4ef]"
        style={{ gridTemplateColumns: template }}
      >
        {showMarker ? <div /> : null}
        {columns.map((column) => (
          <button
            key={column.id}
            type="button"
            onClick={() => onSort(column.id)}
            className={cn(
              "flex h-12 items-center gap-2 font-body text-sm leading-5 text-[#58534b]",
              column.id === "mentee" ? "px-2" : "px-4",
            )}
          >
            {column.label}
            <ChevronDown
              className={cn(
                "size-4 shrink-0",
                sort.column === column.id && sort.direction === "asc"
                  ? "rotate-180"
                  : "",
              )}
              aria-hidden
            />
          </button>
        ))}
      </div>
      {reviews.map((review) => (
        <button
          key={review.id}
          type="button"
          onClick={() => onSelect(review)}
          aria-pressed={selectedId === review.id}
          className={cn(
            "grid w-full border-t border-[#dfddd8] text-left",
            selectedId === review.id ? "bg-[#9f8d6029]" : "bg-white",
          )}
          style={{ gridTemplateColumns: template }}
        >
          {showMarker ? <RowMarker review={review} /> : null}
          {columns.map((column) => (
            <span
              key={column.id}
              className={cn(
                "flex min-h-14 items-center py-3",
                column.id === "mentee" ? "px-2" : "px-4",
              )}
            >
              <Cell
                review={review}
                column={column.id}
                now={now}
                showOpenDays={showOpenDays}
              />
            </span>
          ))}
        </button>
      ))}
      </div>
    </div>
  );
}

function RowMarker({ review }: { review: CareReviewTodo }) {
  return (
    <span className="flex items-center justify-center">
      {review.status === "draft" ? (
        <ClipboardPen className="size-3 text-[#58534b]" aria-label="Draft" />
      ) : review.seen_at == null ? (
        <span className="size-1.5 rounded-full bg-[#0e0d0c]" aria-label="Unseen" />
      ) : null}
    </span>
  );
}

function Cell({
  review,
  column,
  now,
  showOpenDays,
}: {
  review: CareReviewTodo;
  column: CareReviewSortColumn;
  now: DateTime;
  showOpenDays: boolean;
}) {
  switch (column) {
    case "mentee":
      return <MenteeCell review={review} />;
    case "cycle": {
      const reviewType = review.review_type;
      let label: string;
      switch (reviewType) {
        case "intake":
          label = "Intake";
          break;
        case "day_30":
          label = "30-day";
          break;
        case "quarterly":
          label = "90-day";
          break;
        default: {
          const exhaustive: never = reviewType;
          label = exhaustive;
        }
      }
      return (
        <span className="font-body text-base leading-6 text-[#58534b]">
          {label}
        </span>
      );
    }
    case "age":
      return <AgeCell review={review} now={now} />;
    case "submitted":
      return <SubmittedCell review={review} showOpenDays={showOpenDays} />;
    case "assigned":
      return <AssignedCell review={review} />;
    default: {
      const exhaustive: never = column;
      return exhaustive;
    }
  }
}

function MenteeCell({ review }: { review: CareReviewTodo }) {
  const hideIdentity =
    useDevOverridesStore((state) => state.hidePhiPii) === true;
  const changed = presentationChanged(review);
  const name = menteeListLabel(review.mentee, hideIdentity);
  return (
    <span className="flex min-w-0 items-center gap-4">
      <ReviewAvatar user={review.mentee} hideIdentity={hideIdentity} />
      <span className="min-w-0">
        <span className="block truncate font-body text-sm leading-5 text-[#0e0d0c]">
          {name}
        </span>
        <ReviewSubtext review={review} changed={changed} />
      </span>
    </span>
  );
}

function ReviewSubtext({
  review,
  changed,
}: {
  review: CareReviewTodo;
  changed: boolean;
}) {
  if (
    review.status === "submitted" &&
    review.presentation_event === "changed" &&
    review.code
  ) {
    return (
      <span className="flex items-center gap-1 font-body text-xs leading-4 text-[#716a60]">
        Changed to
        <span className="inline-flex h-4 items-center rounded bg-[#f4f3f1] px-1 font-medium text-[#0e0d0c]">
          {review.code}
        </span>
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1 font-body text-xs leading-4 text-[#716a60]">
      {changed ? <Repeat className="size-3" aria-hidden /> : null}
      {changed ? "Change recommended" : "Presentation Review"}
    </span>
  );
}

function AgeCell({ review, now }: { review: CareReviewTodo; now: DateTime }) {
  const label = formatCompactAge(review.created_at, now);
  return (
    <span className="flex items-center gap-2">
      <span
        className={cn(
          "font-body text-sm leading-5",
          review.overdue ? "text-[#dd0b23]" : "text-[#58534b]",
        )}
      >
        {label}
      </span>
      {review.overdue ? (
        <span className="inline-flex h-6 items-center rounded-full bg-[#ffd9df] px-2 font-body text-xs font-medium leading-4 text-[#f93b51]">
          Overdue
        </span>
      ) : null}
    </span>
  );
}

function SubmittedCell({
  review,
  showOpenDays,
}: {
  review: CareReviewTodo;
  showOpenDays: boolean;
}) {
  const days = daysOpen(review.created_at, review.submitted_at);
  return (
    <span className="font-body text-sm leading-5 text-[#58534b]">
      {formatShortDate(review.submitted_at)}
      {showOpenDays && days != null ? (
        <span className={days >= 99 ? "text-[#f93b51]" : undefined}>
          {" "}
          ({days}d)
        </span>
      ) : null}
    </span>
  );
}

function AssignedCell({ review }: { review: CareReviewTodo }) {
  const supervisors = review.supervisors ?? [];
  if (supervisors.length === 0) {
    return <span className="font-body text-sm text-[#716a60]">—</span>;
  }
  return (
    <span className="flex flex-wrap gap-1">
      {supervisors.map((supervisor) => (
        <SupervisorChip key={supervisor.id} name={supervisor.name} />
      ))}
    </span>
  );
}

function SupervisorChip({ name }: { name: string | null }) {
  return (
    <span className="inline-flex items-center rounded-full bg-[#f4f3f1] py-1.5 pl-2 pr-3">
      <span className="font-body text-xs font-medium leading-4 text-black">
        {name ?? "Supervisor"}
      </span>
    </span>
  );
}

function ReviewAvatar({
  user,
  hideIdentity,
}: {
  user: CareUser;
  hideIdentity: boolean;
}) {
  const photo = useSignedMedia(
    hideIdentity ? null : user.profile_picture_url,
    user.id,
  );
  if (!hideIdentity && photo.url) {
    return (
      <img
        src={photo.url}
        alt=""
        className="size-8 shrink-0 rounded-full object-cover"
      />
    );
  }
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#e5e0d2] font-heading text-[11px] font-bold text-[#58534b]">
      {hideIdentity ? "?" : initials(user.name)}
    </span>
  );
}

function initials(name: string | null): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}
